/**
 * Static option lists + prefill normalizers for the v2 setup wizard.
 */

import classificationConfig from '../../../shared-config/business_classification.config.json';
import {
  COMPANY_SIZE_BAND_VALUES,
  companySizeBandDefaultVisibilityMode,
} from '../../../generated/config-enums';
import type { CompanySizeBand, VisibilityMode } from '../../../generated/config-enums';

export type JobFunction =
  | 'sales'
  | 'marketing'
  | 'operations'
  | 'finance'
  | 'hr'
  | 'product'
  | 'engineering'
  | 'it'
  | 'customer_success'
  | 'legal'
  | 'general_management'
  | 'other';

export type JobLevel =
  | 'individual_contributor'
  | 'team_lead'
  | 'manager'
  | 'senior_manager'
  | 'vp'
  | 'c_level';

/**
 * Customer-segmentation chips for Step 1. The canonical customer-*size* axis
 * (`consumer`/`smb`/`enterprise`) — the single vocabulary used by the DB schema,
 * the admin tenant API, `companies.customer_types_override`, and the sales-pipeline
 * seed logic. The B2B/B2C distinction is *derived* from this set, not stored.
 * See `docs/modules/user-management/onboarding-unification-design.md`.
 */
export type CustomerType = 'consumer' | 'smb' | 'enterprise';

/**
 * Headcount bands the enrichment LLM emits, stored on `tenants.company_size_band`.
 * Single-sourced from `shared/config/company_size_band.config.json` — the same
 * config the backend enum binding, the JSON schema, and the DB CHECK derive from.
 * A band missing from the config is silently discarded by {@link normalizeSizeBand}.
 */
export type SizeBand = CompanySizeBand;

export const JOB_FUNCTIONS: { value: JobFunction; labelKey: string }[] = [
  { value: 'sales', labelKey: 'jobFunctions.sales' },
  { value: 'marketing', labelKey: 'jobFunctions.marketing' },
  { value: 'operations', labelKey: 'jobFunctions.operations' },
  { value: 'finance', labelKey: 'jobFunctions.finance' },
  { value: 'hr', labelKey: 'jobFunctions.hr' },
  { value: 'product', labelKey: 'jobFunctions.product' },
  { value: 'engineering', labelKey: 'jobFunctions.engineering' },
  { value: 'it', labelKey: 'jobFunctions.it' },
  { value: 'customer_success', labelKey: 'jobFunctions.customer_success' },
  { value: 'legal', labelKey: 'jobFunctions.legal' },
  { value: 'general_management', labelKey: 'jobFunctions.general_management' },
  { value: 'other', labelKey: 'jobFunctions.other' },
];

export const JOB_LEVELS: { value: JobLevel; labelKey: string }[] = [
  { value: 'individual_contributor', labelKey: 'jobLevels.individual_contributor' },
  { value: 'team_lead', labelKey: 'jobLevels.team_lead' },
  { value: 'manager', labelKey: 'jobLevels.manager' },
  { value: 'senior_manager', labelKey: 'jobLevels.senior_manager' },
  { value: 'vp', labelKey: 'jobLevels.vp' },
  { value: 'c_level', labelKey: 'jobLevels.c_level' },
];

export const CUSTOMER_TYPES: { value: CustomerType; labelKey: string }[] = [
  { value: 'consumer', labelKey: 'setupWizardV2.customerTypes.consumer' },
  { value: 'smb', labelKey: 'setupWizardV2.customerTypes.smb' },
  { value: 'enterprise', labelKey: 'setupWizardV2.customerTypes.enterprise' },
];

/**
 * Coarse industry buckets offered as one-tap fallback chips on Step 1 when the
 * enrichment confidence for industry is low (i.e. the field came back empty).
 * The label IS the stored value — `industry` is a free string on the backend.
 */
export const INDUSTRY_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'Software', labelKey: 'setupWizardV2.industries.software' },
  { value: 'Services', labelKey: 'setupWizardV2.industries.services' },
  { value: 'Retail', labelKey: 'setupWizardV2.industries.retail' },
  { value: 'Manufacturing', labelKey: 'setupWizardV2.industries.manufacturing' },
  { value: 'Healthcare', labelKey: 'setupWizardV2.industries.healthcare' },
  { value: 'Logistics', labelKey: 'setupWizardV2.industries.logistics' },
];

/** Config-ordered size chips for Step 1. Display labels stay in the locale files. */
export const SIZE_BANDS: { value: SizeBand; labelKey: string }[] = COMPANY_SIZE_BAND_VALUES.map(
  (value) => ({ value, labelKey: `setupWizardV2.sizeBands.${value}` }),
);

/**
 * Business-model "what do you sell?" flags. Single-sourced from
 * `business_classification.config.json` (the same taxonomy the backend
 * `ProductsTenantSettings.sells_*` columns use) so the wizard can't drift from
 * it. `flag` is the `sells_*` column; `modelValue` keys the existing
 * `common:businessClassification.businessModel.*` i18n labels.
 */
export type SellsFlag =
  | 'sells_physical_products'
  | 'sells_services_by_project'
  | 'sells_services_by_time'
  | 'sells_subscriptions'
  | 'sells_logistics_services';

export const BUSINESS_TYPES: { flag: SellsFlag; modelValue: string }[] =
  classificationConfig.business_models.map((model) => ({
    flag: model.setting_flag as SellsFlag,
    modelValue: model.value,
  }));

export type SellsFlags = Record<SellsFlag, boolean>;

/**
 * The `business_models[].value` entries behind the checked "what do you sell?"
 * chips — the wire format `GET /setup-wizard/recommended-modules` expects
 * (`?business_model=physical_products&…`). Config-ordered, so the query string
 * is stable.
 */
export function selectedBusinessModels(flags: SellsFlags): string[] {
  return BUSINESS_TYPES.filter((type) => flags[type.flag]).map((type) => type.modelValue);
}

export const EMPTY_SELLS_FLAGS: SellsFlags = {
  sells_physical_products: false,
  sells_services_by_project: false,
  sells_services_by_time: false,
  sells_subscriptions: false,
  sells_logistics_services: false,
};

/**
 * Best-effort derive the business-model flags from the free-text industry the
 * AI enrichment (or an industry chip) produced. This powers the "derived from
 * industry where possible, asked only when ambiguous" behaviour: the chips open
 * pre-selected when we can infer them, and the user confirms/adjusts. Returns
 * all-false when nothing matches (the user then picks). Substring-matched and
 * case-insensitive — the same coarse buckets the backend industry→verticals
 * mapping uses (`shared/utils/industry_mapping.py`); keep the two in sync.
 */
export function deriveSellsFlags(industry: string | null | undefined): SellsFlags {
  const flags = { ...EMPTY_SELLS_FLAGS };
  if (!industry) return flags;
  const v = industry.toLowerCase();
  const has = (...needles: string[]) => needles.some((n) => v.includes(n));

  if (has('logistic', 'freight', 'shipping', 'transport', '3pl', 'warehous', 'courier', 'customs')) {
    flags.sells_logistics_services = true;
  }
  if (has('software', 'saas', 'subscription', 'media', 'membership')) {
    flags.sells_subscriptions = true;
  }
  if (has('consult', 'agency', 'marketing', 'engineering', 'architect', 'design', 'project')) {
    flags.sells_services_by_project = true;
  }
  if (has('legal', 'account', 'finance', 'insurance', 'education', 'training', 'real estate', 'hospitality', 'medical')) {
    flags.sells_services_by_time = true;
  }
  if (has('retail', 'manufactur', 'product', 'goods', 'food', 'beverage', 'apparel', 'fashion', 'electronics', 'wholesale', 'distribut', 'pharma', 'automotive', 'furniture', 'beauty', 'cosmetic', 'agricultur', 'energy', 'chemical', 'construction')) {
    flags.sells_physical_products = true;
  }
  return flags;
}

/**
 * The two visibility choices the wizard offers per configurable module. The full
 * `team` mode is reserved for the settings page — onboarding keeps a simple
 * shared-vs-private default. Checked against the single-sourced `VisibilityMode`
 * union so a config rename breaks the build rather than silently drifting.
 */
export const WIZARD_VISIBILITY_MODES = [
  'everyone',
  'owner_and_shared',
] as const satisfies readonly VisibilityMode[];

export type WizardVisibilityMode = (typeof WIZARD_VISIBILITY_MODES)[number];

/**
 * The visibility mode a company of this size gets by default. Small companies
 * share everything; medium-and-up start restricted. Single-sourced from the size-band
 * config, and the backend derives the identical default from the same config for
 * tenants created outside the wizard.
 *
 * No cast: the generated map's value type is the exact union of the modes present
 * in the config, so widening a band's default to `team`/`custom` — which this
 * wizard can neither render nor summarise — fails the build here rather than
 * silently rendering a raw i18n key.
 */
export function defaultVisibilityModeForBand(band: SizeBand | null): WizardVisibilityMode {
  if (!band) return 'everyone';
  return companySizeBandDefaultVisibilityMode[band];
}

/**
 * Build the create-tenant `visibility_modes` payload (Sharing Infra P-4) from the
 * wizard answers: one EXPLICIT entry per SELECTED module that exposes a
 * `visibility_object_type`, falling back to the size band's default where the admin
 * left the Advanced section untouched. Returns null when no selected module is
 * visibility-configurable.
 *
 * `everyone` entries are sent, not dropped. The backend still writes no row for
 * `everyone` (absent row == everyone), but it now *derives* an unanswered module's
 * mode from the band — so omitting an `everyone` answer would silently upgrade a
 * large company's deliberate "share with everyone" choice to `owner_and_shared`.
 */
export function buildVisibilityModesPayload(
  modules: { id: string; visibility_object_type?: string | null }[],
  selectedModuleIds: string[],
  answers: Record<string, string>,
  band: SizeBand | null,
): Record<string, string> | null {
  const fallback = defaultVisibilityModeForBand(band);
  const entries = modules
    .filter((m) => m.visibility_object_type && selectedModuleIds.includes(m.id))
    .map((m) => {
      const objectType = m.visibility_object_type as string;
      return [objectType, answers[objectType] ?? fallback] as const;
    });
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

const VALID_SIZE_BANDS = new Set<string>(SIZE_BANDS.map((b) => b.value));

/** Coerce a free-form enrichment size_band value to a known band, or null. */
export function normalizeSizeBand(value: string | null): SizeBand | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  return VALID_SIZE_BANDS.has(v) ? (v as SizeBand) : null;
}

/**
 * Map the enrichment's free-form `customer_types` strings onto the canonical
 * customer-*size* axis (`consumer`/`smb`/`enterprise`). The LLM returns arbitrary
 * descriptors, so we match on substrings:
 *  - "consumer" / "b2c" / "individual" / "retail" → `consumer`
 *  - "enterprise" / "large" / "corporation" → `enterprise`
 *  - generic "business" / "b2b" / "smb" / "company" → `smb`
 * "marketplace" / "platform" describe a business model, not a customer size, so
 * they no longer map to a stored value (the B2B/B2C distinction is derived).
 */
export function normalizeCustomerTypes(values: string[] | null): CustomerType[] {
  if (!values) return [];
  const out = new Set<CustomerType>();
  for (const raw of values) {
    const v = raw.toLowerCase();
    if (v.includes('consumer') || v.includes('b2c') || v.includes('individual') || v.includes('retail')) {
      out.add('consumer');
    }
    if (v.includes('enterprise') || v.includes('large') || v.includes('corporation') || v.includes('corporate')) {
      out.add('enterprise');
    }
    if (v.includes('smb') || v.includes('small') || v.includes('medium') || v.includes('mid-market') || v.includes('midmarket') || v.includes('b2b') || v.includes('business') || v.includes('compan')) {
      out.add('smb');
    }
  }
  return Array.from(out);
}
